// Shared check-in helpers for private bookings (single guest count, no ticket types).
// States: pending (0 checked in) → partial (some) → in (all checked in) → out (checked out).

export const CHECK_IN_STATE = {
  PENDING: "pending",
  PARTIAL: "partial",
  IN: "in",
  OUT: "out"
}

// Derive the row state from counts.
//   pending : nobody checked in
//   in      : everyone checked in, nobody checked out → next action is check-out
//   out     : everyone checked out → next action is reset
//   partial : any in-between (some checked in, or some—but not all—checked out) → dropdown
export const deriveCheckInState = (checkedInCount = 0, totalGuests = 0, checkedOutCount = 0) => {
  if (totalGuests > 0 && checkedOutCount >= totalGuests) return CHECK_IN_STATE.OUT
  if (checkedInCount <= 0) return CHECK_IN_STATE.PENDING
  if (checkedInCount >= totalGuests && checkedOutCount <= 0) return CHECK_IN_STATE.IN
  return CHECK_IN_STATE.PARTIAL
}

// Tooltip copy per state (matches the design's row hints).
export const CHECK_IN_TOOLTIP = {
  [CHECK_IN_STATE.PENDING]: "Click to check-in",
  [CHECK_IN_STATE.PARTIAL]: "Partially checked-in, click to check-in or out",
  [CHECK_IN_STATE.IN]: "Checked-in, click to check out",
  [CHECK_IN_STATE.OUT]: "Checked out, click to reset"
}

// Read-only status labels (no "click to…" — used where check-in is display-only).
export const CHECK_IN_STATUS_LABEL = {
  [CHECK_IN_STATE.PENDING]: "Not checked in",
  [CHECK_IN_STATE.PARTIAL]: "Partially checked in",
  [CHECK_IN_STATE.IN]: "Checked in",
  [CHECK_IN_STATE.OUT]: "Checked out"
}

// Normalise a booking's guest-type breakdown into the shape the check-in/out modals render:
// [{ key, label, total, checkedInCount, checkedOutCount }]. Uses the backend `guest_types`
// array when present; otherwise a single synthetic "Guests" row from the aggregate counts, so
// the modals keep working before per-type data lands.
export const buildGuestTypes = (rawTypes, fallbackTotal = 0, fallbackCheckedIn = 0, fallbackCheckedOut = 0) => {
  if (Array.isArray(rawTypes) && rawTypes.length > 0) {
    return rawTypes.map((type) => ({
      key: String(type.key ?? type.id ?? type.label),
      label: type.label ?? type.name ?? "Guests",
      total: type.total ?? type.count ?? 0,
      checkedInCount: type.checked_in_count ?? 0,
      checkedOutCount: type.checked_out_count ?? 0
    }))
  }
  return [{ key: "guests", label: "Guests", total: fallbackTotal, checkedInCount: fallbackCheckedIn, checkedOutCount: fallbackCheckedOut }]
}

export const sumCheckedIn = (guestTypes = []) => guestTypes.reduce((sum, type) => sum + (type.checkedInCount ?? 0), 0)
export const sumCheckedOut = (guestTypes = []) => guestTypes.reduce((sum, type) => sum + (type.checkedOutCount ?? 0), 0)
export const sumGuests = (guestTypes = []) => guestTypes.reduce((sum, type) => sum + (type.total ?? 0), 0)

// ---------------------------------------------------------------------------
// Group model (private bookings): the whole booking is checked in / out as ONE
// unit. The check-in/out modal shows a single row for the booking (client name,
// guest count, booking-level documents) with one IN/OUT toggle for the group —
// not per-individual guests and not per-type counts.
// ---------------------------------------------------------------------------

// Group check-in state (all-or-nothing).
export const GUEST_STATE = {
  PENDING: "pending",
  IN: "in",
  OUT: "out"
}

// Normalise a booking's documents into the shape the modal renders.
const normaliseDocuments = (documents) =>
  (Array.isArray(documents) ? documents : []).map((doc, index) => ({
    id: String(doc.document_id ?? doc.id ?? index),
    title: doc.title ?? doc.name ?? "Document",
    isSigned: doc.is_signed ?? false
  }))

// Build the single group row the modal renders for a booking. Returned as a one-element
// array so the modal/flow can iterate uniformly. The group state uses the backend
// `check_in_state` (pending/in/out) when provided; otherwise it's derived from the aggregate
// counts (checked-out wins over checked-in; all-or-nothing).
export const buildGroupRow = ({
  id,
  name,
  guestCount = 0,
  checkedIn = 0,
  checkedOut = 0,
  state,
  docsSigned = 0,
  docsTotal = 0,
  documents = []
} = {}) => {
  let groupState = state
  if (!groupState) {
    groupState = GUEST_STATE.PENDING
    if (guestCount > 0 && checkedOut >= guestCount) groupState = GUEST_STATE.OUT
    else if (checkedIn > 0) groupState = GUEST_STATE.IN
  }
  return [
    {
      id: String(id ?? "group"),
      name: name || "Guests",
      guestCount,
      phone: null,
      type: null,
      state: groupState,
      docsSigned,
      docsTotal,
      documents: normaliseDocuments(documents)
    }
  ]
}

export const countGuestsIn = (guests = []) => guests.filter((guest) => guest.state === GUEST_STATE.IN).length
export const countGuestsOut = (guests = []) => guests.filter((guest) => guest.state === GUEST_STATE.OUT).length

// A guest can be checked in only once its required documents are signed.
export const guestWaiversComplete = (guest) => (guest.docsTotal ?? 0) === 0 || (guest.docsSigned ?? 0) >= (guest.docsTotal ?? 0)
export const guestMissingDocuments = (guest) => (guest.documents ?? []).filter((doc) => !doc.isSigned)

// Row state derived from the per-guest list (drives the CheckInBadge).
export const deriveStateFromGuests = (guests = []) => {
  if (guests.length === 0) return CHECK_IN_STATE.PENDING
  const inCount = countGuestsIn(guests)
  const outCount = countGuestsOut(guests)
  if (outCount >= guests.length) return CHECK_IN_STATE.OUT
  if (inCount === 0 && outCount === 0) return CHECK_IN_STATE.PENDING
  if (inCount >= guests.length) return CHECK_IN_STATE.IN
  return CHECK_IN_STATE.PARTIAL
}
